import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  MenuItem,
  TextField,
  Typography
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getInternalChatTexts } from "../../translate/pageTexts";

const buildUserLabel = user =>
  [user?.name, user?.email].filter(Boolean).join(" - ");

const InternalChatConversationDialog = ({
  open,
  onClose,
  users,
  onCreated,
  canManageGroups = false
}) => {
  const texts = getInternalChatTexts();
  const [mode, setMode] = useState("direct");
  const [directUserId, setDirectUserId] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupUserIds, setGroupUserIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const sortedUsers = useMemo(
    () =>
      Array.isArray(users)
        ? [...users].sort((left, right) =>
            String(left?.name || "").localeCompare(String(right?.name || ""))
          )
        : [],
    [users]
  );

  useEffect(() => {
    if (!open) {
      setMode("direct");
      setDirectUserId("");
      setGroupName("");
      setGroupUserIds([]);
    }
  }, [open]);

  useEffect(() => {
    if (!canManageGroups && mode === "group") {
      setMode("direct");
    }
  }, [canManageGroups, mode]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const { data } =
        mode === "direct"
          ? await api.post("/internal-chats/direct", {
              userId: Number(directUserId)
            })
          : await api.post("/internal-chats/group", {
              name: groupName,
              userIds: groupUserIds.map(Number)
            });

      onCreated(data);
      onClose();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const isDisabled =
    saving ||
    (mode === "direct" && !directUserId) ||
    (mode === "group" &&
      (!String(groupName || "").trim() || groupUserIds.length === 0));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="internal-chat-dialog-title"
    >
      <DialogTitle id="internal-chat-dialog-title">
        {texts.newInternalConversation}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={texts.type}
          value={mode}
          onChange={event => setMode(event.target.value)}
        >
          <MenuItem value="direct">{texts.direct}</MenuItem>
          {canManageGroups ? <MenuItem value="group">{texts.group}</MenuItem> : null}
        </TextField>

        {mode === "direct" ? (
          <TextField
            select
            fullWidth
            margin="dense"
            variant="outlined"
            label={texts.chooseUser}
            value={directUserId}
            onChange={event => setDirectUserId(Number(event.target.value))}
          >
            {sortedUsers.map(user => (
              <MenuItem key={user.id} value={user.id}>
                {buildUserLabel(user)}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={texts.groupName}
              value={groupName}
              onChange={event => setGroupName(event.target.value)}
            />
            <TextField
              select
              fullWidth
              margin="dense"
              variant="outlined"
              label={texts.participants}
              value={groupUserIds}
              onChange={event =>
                setGroupUserIds(
                  Array.isArray(event.target.value)
                    ? event.target.value.map(Number)
                    : []
                )
              }
              SelectProps={{
                multiple: true,
                renderValue: selected =>
                  sortedUsers
                    .filter(user => selected.includes(user.id))
                    .map(user => user.name)
                    .join(", ")
              }}
            >
              {sortedUsers.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  <Checkbox checked={groupUserIds.includes(user.id)} />
                  <ListItemText primary={buildUserLabel(user)} />
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="body2" color="textSecondary">
              {texts.groupAutoInclude}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {texts.cancel}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={isDisabled}
        >
          {texts.createConversation}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InternalChatConversationDialog;
